<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyBankAccountFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    bankAccount: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.bankAccount,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('bankAccount');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_xnt9qzfd5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8uew8zws8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_xnt9qzfd5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8uew8zws8],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_8uew8zws8,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_8uew8zws8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8uew8zws8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="account_number"
      :label="pgettext('CompanyBankAccount', 'Account Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="account_number"
        v-model="bankAccount.account_number"
        required
        autofocus
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="description"
      :label="pgettext('CompanyBankAccount', 'Description')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="description" v-model="bankAccount.description">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="iban"
      :label="pgettext('CompanyBankAccount', 'IBAN')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="iban" v-model="bankAccount.iban"> </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      name="valid_from"
      :label="pgettext('CompanyBankAccount', 'Validity')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      contentClass="row"
    >
      <div class="col">
        <ItpFormDatetime name="valid_from" v-model="bankAccount.valid_from">
        </ItpFormDatetime>
      </div>
      <div class="col">
        <ItpFormDatetime name="valid_to" v-model="bankAccount.valid_to">
        </ItpFormDatetime>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="is_default"
      :label="pgettext('Company', 'Default')"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox name="is_default" v-model="bankAccount.is_default">
      </ItpFormCheckbox>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
